import React, { useContext, useEffect, useState } from "react";

import { Alert, Stack, Typography } from "@mui/material";

import { AdminRestriction } from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";

import { updateOrgRestriction } from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import RestrictionValueEditor, {
  RestrictionOneof,
} from "components/shared/restrictions/restriction-value-editor";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

interface EditOrgRestrictionProps {
  open: boolean;
  organizationId: string;
  regionCode: string;
  restrictionName: string;
  restriction: AdminRestriction;
  onClose: () => void;
  onSuccess: () => void;
}

// EditOrgRestriction is the per-org override write surface — a thin
// ConfirmDialog wrapper around the shared RestrictionValueEditor, following
// the same dumb-modal-calls-onSuccess pattern as ProvisionAICreditPack. It
// writes via UpdateOrgRestriction (FG-7722), which persists a genuine
// per-subject override for the restriction names this is gated to (see
// constants/restrictions.ts).
const EditOrgRestriction = ({
  open,
  organizationId,
  regionCode,
  restrictionName,
  restriction,
  onClose,
  onSuccess,
}: EditOrgRestrictionProps) => {
  const { showError } = useContext(SnackbarContext);

  const [draft, setDraft] = useState<RestrictionOneof>(
    restriction.restriction as RestrictionOneof,
  );
  const [isSaving, setIsSaving] = useState(false);

  // Reset the draft whenever a different row is opened, so a previous edit
  // doesn't leak into the next restriction's dialog.
  useEffect(() => {
    setDraft(restriction.restriction as RestrictionOneof);
  }, [restriction]);

  const isUnsupportedShape =
    draft.case !== "feature" &&
    draft.case !== "timeWindow" &&
    draft.case !== "quota";

  const handleConfirm = async () => {
    if (isUnsupportedShape) return;

    setIsSaving(true);
    try {
      await updateOrgRestriction(
        {
          organizationId,
          restrictionName,
          restriction: new AdminRestriction({
            ...restriction,
            restriction: draft,
          }),
        },
        callOptionsHeaders(regionCode, organizationId),
      );
      onSuccess();
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDraft(restriction.restriction as RestrictionOneof);
    onClose();
  };

  return (
    <ConfirmDialog
      title={`Edit ${restrictionName.replace("RESTRICTION_NAME_", "")}`}
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      onConfirm={handleConfirm}
      onCancel={handleClose}
      cancelBtnLabel="Cancel"
      confirmBtnLabel="Save"
      disabled={isSaving || isUnsupportedShape}
      disableCloseOnConfirm
      disableBackgroundClick={isSaving}
    >
      <Stack spacing={2} sx={{ mt: 1, width: "100%" }}>
        <Alert severity="warning">
          This writes a per-org override, taking precedence over the tier policy
          for this org until removed.
        </Alert>
        {restrictionName === "RESTRICTION_NAME_SSO_ENFORCE_SSO_ONLY" && (
          <Alert severity="warning">
            This is never enforced server-side for any org — toggling it has no
            effect on whether SSO-only login is actually required.
          </Alert>
        )}
        {restrictionName === "RESTRICTION_NAME_SCIM_FEATURE_GATE" && (
          <Alert severity="warning">
            Restricting this immediately breaks the org&apos;s live IdP sync
            with a silent 403. There is no grace period.
          </Alert>
        )}
        {restrictionName ===
          "RESTRICTION_NAME_DELEGATED_MANAGEMENT_FEATURE_GATE" && (
          <Alert severity="warning">
            Only read by a frontend hint — actual access is controlled by a
            LaunchDarkly flag and existing role assignments, neither of which
            this setting touches.
          </Alert>
        )}
        {restrictionName === "RESTRICTION_NAME_GROUPS_CREATE_DYNAMIC_GROUP" && (
          <Alert severity="warning">
            This only controls whether the org can create new dynamic groups.
            Existing dynamic groups keep auto-updating membership regardless of
            this setting.
          </Alert>
        )}
        {restrictionName === "RESTRICTION_NAME_SITES_MANAGE_RULES" && (
          <Alert severity="warning">
            This only controls whether the org can create new site rules.
            Existing site rules keep executing regardless of this setting.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          Organisation: {organizationId}
        </Typography>
        <RestrictionValueEditor
          restriction={draft}
          onChange={setDraft}
          disabled={isSaving}
        />
      </Stack>
    </ConfirmDialog>
  );
};

export default EditOrgRestriction;
